import React from 'react';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { BaseUser } from '@common/react/objects/BaseUser';
import { BaseApplicationState } from '@common/react/store';

import { getImagePath } from '@app/objects/Image';
import '@app/scss/components/customAvatar.scss';

interface CustomAvatarProps {
	src: string | undefined | null;
	alt?: string;
	errorAvatar?: any;
	wrapClassName?: string;
	className?: string;
	maxWidth?: number;
	maxHeight?: number;
	lazy?: boolean;
	offset?: number;
	fetchPriority?: 'high' | 'low' | 'auto';
	onClick?: () => void;
}

const defaultVetAvatar = require('@app/images/vetDefAvatar.svg');

const CustomAvatar: React.FC<CustomAvatarProps> = ({
	src,
	alt = 'Avatar',
	errorAvatar = defaultVetAvatar,
	wrapClassName = '',
	className = '',
	maxWidth,
	maxHeight,
	lazy = true,
	offset = 300,
	fetchPriority,
	onClick,
}) => {
	const hostOptions = useSelector((state: BaseApplicationState<BaseUser>) => state.hostOptions.item);
	const [imgSrc, setImgSrc] = React.useState<string>(getImagePath(src, maxHeight, maxWidth, hostOptions?.hostServer || ''));
	const [hasError, setHasError] = React.useState<boolean>(false);
	const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
	const [isTimeout, setIsTimeout] = React.useState<boolean>(false);
	const [isVisible, setIsVisible] = React.useState<boolean>(!lazy);
	const [timer, setTimer] = React.useState<NodeJS.Timeout | undefined>(undefined);

	const clearTimer = () => {
		if (timer) {
			clearTimeout(timer);
			setTimer(undefined);
		}
	};

	React.useEffect(() => {
		setImgSrc(getImagePath(src, maxHeight, maxWidth, hostOptions?.hostServer || ''));
		setHasError(false);
		setIsLoaded(false);
		setIsTimeout(false);
		setIsVisible(!lazy);
		clearTimer();
	}, [src, maxWidth, maxHeight, lazy]);

	React.useEffect(() => {
		if (isVisible && !isLoaded && !hasError && !isTimeout && !timer) {
			const newTimer = setTimeout(() => {
				setIsTimeout(true);
			}, 5000);
			setTimer(newTimer);
		}

		return () => clearTimer();
	}, [isVisible, isLoaded, hasError, isTimeout, timer]);

	const showErrorImage = hasError || isTimeout;
	const showLoader = !isLoaded && !showErrorImage;

	return (
		<div key={imgSrc} className={`relative custom-avatar ${wrapClassName}`}>
			{showLoader && <BounceLoader className="loading-component__m0-auto" />}

			{lazy ? (
				<InView
					as="div"
					threshold={0}
					rootMargin={`${offset}px`}
					onChange={(inView) => {
						if (inView) {
							setIsVisible(true);
						}
					}}
				>
					{isVisible && (
						<>
							<img
								src={imgSrc}
								alt={alt}
								className={className}
								style={{
									display: !showErrorImage && isLoaded ? 'block' : 'none',
								}}
								onLoad={() => {
									setIsLoaded(true);
									clearTimer();

									if (isTimeout) setIsTimeout(false);
								}}
								onError={() => {
									setHasError(true);
									clearTimer();
								}}
								onClick={onClick}
								fetchPriority={fetchPriority}
							/>

							{showErrorImage && (
								<img
									src={errorAvatar}
									alt={alt}
									className={className}
									style={{ display: 'block' }}
									onClick={onClick}
									fetchPriority={fetchPriority}
								/>
							)}
						</>
					)}
				</InView>
			) : (
				<>
					<img
						src={imgSrc}
						alt={alt}
						className={className}
						style={{
							display: !showErrorImage && isLoaded ? 'block' : 'none',
						}}
						onLoad={() => {
							setIsLoaded(true);
							clearTimer();

							if (isTimeout) setIsTimeout(false);
						}}
						onError={() => {
							setHasError(true);
							clearTimer();
						}}
						onClick={onClick}
						fetchPriority={fetchPriority}
					/>

					{showErrorImage && (
						<img
							src={errorAvatar}
							alt={alt}
							className={className}
							style={{ display: 'block' }}
							onClick={onClick}
							fetchPriority={fetchPriority}
						/>
					)}
				</>
			)}
		</div>
	);
};

export default CustomAvatar;
